import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import merge from 'lodash.merge';
import moment from 'moment';
import { history } from '../../configureStore';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { getVariableValue, setVariableValue } from '../../utils/globalVariableHelper';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, getSizesBasedOnScreen, executeFunction, isClickInsidePortal } from '../../utils/base';
import { ChartComponent } from '../../types/base';
import { pageRequest, submitFormRequest, mergeStyleRequest, updateVariableRequest } from '../../store/page/actions';
import { StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, Breakpoints } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import ChartRenderer from '../ChartRenderer/ChartRenderer';

const StyledChartContainer = styled.div<{
  $shape?: any;
  $baseScreenResolution?: any;
  $skipStates?: boolean;
}>`
  z-index: 1;
  cursor: pointer;
  position: relative;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
`;

interface ChartSettings {
  ref?: string;
  data?: string;
}

interface ChartComponentProps  extends ChartComponent {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
  'chart-items': any[];
  settings?: ChartSettings;
}

const Chart: React.FC<ChartComponentProps & { title: string, pageSettings: any }> = ({ 
  id, style: stDraft,
  events: eventsDraft, 
  pageSettings,
  dataLake,
  activeState: activeStateDraft,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentLoading,
  enableLocalShimmer,
  valueOriginal,
  cache,
  settings,
  'chart-items': items,
 }) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { style: styleDraft, events, activeState, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, activeState: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, activeState: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'switcher') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);
  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? (cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal))) ? true : false : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, valueOriginal, parentLoading]);
  const variableData = useSelector(selectVariableData);
  const localVariableData = useSelector(selectLocalVariableData);
  // First useMemo to merge active state styles with base style
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const { 
    on_change: clickUpdates, 
    on_launch: launchUpdates, 
    on_hover: hoverUpdates, 
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
  } = events || {};
  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    if (clickUpdates) {
      e.stopPropagation();
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id,
             }, filterLoadingState));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  const handleHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  
  }

  const handleUnhover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);

      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            // const newTimeout = setTimeout(() => {
            //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            // }, config?.delay * 1000 || 0);

            // timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('mousedown', handleUnclick);
    return () => {
      document.removeEventListener('mousedown', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
  ]);


  if (renderSceleton && style) {
    return <ContainerSkeleton 
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={activeState === 'disabled' || activeState === 'loading'}
    />;
  }

  // Calculate dimensions from style
  const chartWidth = style?.width || '100%';
  const chartHeight = style?.height || '100%';

  // Get chart data from settings
  const chartDataSource = settings?.data;

  return (
    <StyledChartContainer
      ref={containerRef}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      data-component="chart"
      className={combineClassNames('disableFocus', style.class)}
      onClick={handleClick}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
    >
      {items && items.length > 0 && (
        <ChartRenderer
          chartItems={items}
          settingsData={chartDataSource}
          localVariables={dataLake?.data}
        />
      )}
    </StyledChartContainer>
  );
};

export default Chart;